import { useState } from "react";
import classNames from "classnames";
import ChevronRightNarrow from "@/assets/svgs/chevron-right-narrow.svg";
import DealTagIcon from "@/assets/svgs/deal-tag.svg";
import type { WorkCondition, WorkFormat } from "@/types/work.types";
import { Link } from "@/components/shared/inputs/link/link";
import ConditionRatingsModal from "../condition-ratings-modal";
import {
  formatWorkConditionSelectionPrice,
  getWorkConditionAvailability,
} from "../work-condition-availability";
import {
  formatWorkPrice,
  formatWorkPriceRange,
  getConditionDescription,
} from "../work-price";
import {
  WorkLoyaltyBonusBadge,
  WorkLoyaltyBonusMessage,
} from "../work-loyalty-bonus";
import styles from "./work-copy-selector.module.scss";

export interface WorkCopySelectorProps {
  formats: WorkFormat[];
  selectedFormat: WorkFormat;
  selectedCondition: WorkCondition;
  totalEditions: number;
  lowestEditionPrice: number | null;
  allEditionsUrl: string;
  onSelectFormat: (idMedia: number) => void;
  onSelectCondition: (idQuality: number) => void;
  onOpenCopyDrawer: () => void;
}

const WorkCopySelector = ({
  formats,
  selectedFormat,
  selectedCondition,
  totalEditions,
  lowestEditionPrice,
  allEditionsUrl,
  onSelectFormat,
  onSelectCondition,
  onOpenCopyDrawer,
}: WorkCopySelectorProps) => {
  const [hoveredCondition, setHoveredCondition] =
    useState<WorkCondition | null>(null);
  const description = getConditionDescription(
    hoveredCondition ?? selectedCondition,
  );
  const selectedDescription = getConditionDescription(selectedCondition);
  const showBestAvailable = Boolean(
    selectedCondition.bestAvailableMessage &&
    selectedCondition.quantity > selectedCondition.inCartQuantity,
  );
  const selectedIsAvailable =
    getWorkConditionAvailability(selectedCondition) === "available";
  const editionPriceText =
    lowestEditionPrice != null && lowestEditionPrice > 0
      ? ` from ${formatWorkPrice(lowestEditionPrice)}`
      : "";
  return (
    <section className={styles.root} aria-label="Choose a copy">
      <div className={styles.smallAndUpSelectors}>
        <div className={styles.labelRow}>
          <span>Formats</span>
          <Link
            href={allEditionsUrl}
            linkType="legacy"
            className={styles.allEditionsLink}
            underline="hover"
          >
            See all {totalEditions} editions and languages{editionPriceText}
          </Link>
        </div>
        <div
          className={styles.optionGrid}
          role="radiogroup"
          aria-label="Formats"
        >
          {formats.map((format) => (
            <button
              key={format.idMedia}
              type="button"
              role="radio"
              aria-checked={format.idMedia === selectedFormat.idMedia}
              className={classNames(styles.option, {
                [styles.selected]: format.idMedia === selectedFormat.idMedia,
              })}
              onClick={() => onSelectFormat(format.idMedia)}
            >
              <span className={styles.optionTitle}>{format.name}</span>
              <span>{formatWorkPriceRange(format)}</span>
            </button>
          ))}
        </div>

        <p className={styles.selectorLabel}>Conditions</p>
        <div
          className={classNames(styles.optionGrid, {
            [styles.hasLoyaltyBonus]: selectedFormat.conditions.some(
              (condition) => condition.loyaltyBonusPoints > 0,
            ),
          })}
          role="radiogroup"
          aria-label="Conditions"
        >
          {selectedFormat.conditions.map((condition) => {
            const availability = getWorkConditionAvailability(condition);
            const isAvailable = availability === "available";
            const isUnavailable = availability === "unavailable";
            return (
              <div
                key={condition.idQuality}
                className={styles.conditionOptionWrapper}
                onMouseEnter={() => setHoveredCondition(condition)}
                onMouseLeave={() => setHoveredCondition(null)}
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={
                    condition.idQuality === selectedCondition.idQuality
                  }
                  className={classNames(styles.option, styles.conditionOption, {
                    [styles.selected]:
                      condition.idQuality === selectedCondition.idQuality,
                  })}
                  disabled={isUnavailable}
                  onClick={() => onSelectCondition(condition.idQuality)}
                  onFocus={() => setHoveredCondition(condition)}
                  onBlur={() => setHoveredCondition(null)}
                >
                  <span className={styles.optionTitle}>{condition.name}</span>
                  <span
                    className={classNames(styles.conditionPrice, {
                      [styles.thriftDealPrice]:
                        condition.isThriftDeal && isAvailable,
                    })}
                  >
                    {formatWorkConditionSelectionPrice(condition)}
                    {condition.isThriftDeal && isAvailable && (
                      <DealTagIcon
                        className={styles.dealIcon}
                        role="img"
                        aria-label="Thrift Deal"
                      />
                    )}
                  </span>
                  <WorkLoyaltyBonusBadge
                    points={condition.loyaltyBonusPoints}
                    active={condition.idQuality === selectedCondition.idQuality}
                    className={styles.loyaltyBonusBadge}
                  />
                </button>
              </div>
            );
          })}
        </div>
        <p className={styles.conditionDescription}>
          {description && (
            <>
              {description} <ConditionRatingsModal />
            </>
          )}
        </p>
        <WorkLoyaltyBonusMessage
          points={selectedCondition.loyaltyBonusPoints}
          condition={selectedCondition.name}
        />
      </div>

      <div className={styles.belowSmallSelectors}>
        <div className={styles.belowSmallLabelRow}>
          <p>Formats</p>
          <Link href={allEditionsUrl} linkType="legacy" underline="hover">
            See all {totalEditions} Formats &amp; Editions{editionPriceText}
          </Link>
        </div>
        <button
          type="button"
          className={styles.belowSmallSelection}
          onClick={onOpenCopyDrawer}
        >
          <span className={styles.belowSmallSelectionValue}>
            <strong>{selectedFormat.name}</strong>{" "}
            <span>({formatWorkPriceRange(selectedFormat)})</span>
          </span>
          <ChevronRightNarrow
            className={styles.belowSmallChevron}
            aria-hidden
          />
        </button>
        <p className={styles.belowSmallSectionLabel}>Conditions</p>
        <button
          type="button"
          className={styles.belowSmallSelection}
          onClick={onOpenCopyDrawer}
        >
          <span className={styles.belowSmallSelectionValue}>
            {selectedCondition.idQuality === 0 ? (
              <strong>Unavailable</strong>
            ) : (
              <>
                <strong>
                  {selectedCondition.name}
                  {showBestAvailable && "*"}:
                </strong>{" "}
                <span
                  className={classNames(styles.selectedConditionPrice, {
                    [styles.thriftDealPrice]:
                      selectedCondition.isThriftDeal && selectedIsAvailable,
                  })}
                >
                  {formatWorkConditionSelectionPrice(selectedCondition)}
                </span>
                <WorkLoyaltyBonusBadge
                  points={selectedCondition.loyaltyBonusPoints}
                  active
                  className={styles.belowSmallLoyaltyBonusBadge}
                />
              </>
            )}
          </span>
          <ChevronRightNarrow
            className={styles.belowSmallChevron}
            aria-hidden
          />
        </button>
        {showBestAvailable && (
          <p className={styles.belowSmallBestAvailable}>
            {selectedCondition.bestAvailableMessage}
          </p>
        )}
        {selectedDescription && (
          <p className={styles.conditionDescription}>
            {selectedDescription} <ConditionRatingsModal />
          </p>
        )}
        <WorkLoyaltyBonusMessage
          points={selectedCondition.loyaltyBonusPoints}
          condition={selectedCondition.name}
        />
      </div>
    </section>
  );
};

export default WorkCopySelector;
